/**
 * 投注与奖池的两段式分流。
 *
 * <p>这一页此前只有一行字：「每一期：80% 进奖池，20% 留给下一期」。80/20 这两个
 * 数字没错，错的是「每一期」—— 按 `MilharJackpotMath.settle()`，80/20 是**有人中
 * 头奖时**奖池怎么分；**没人中头奖时整个池子 100% 滚到下一期，根本不分**。而每一期
 * 真正在发生的那件事（投注额的 25% 进池）当时一个字都没提。
 *
 * <p>另一半问题是：把「25% 进奖池」单独摆出来，读者的第一反应是平台拿走了 75%。
 * 真实分配是**三份**：25% 进奖池、`rtp − 25` 直接赔给当期中奖的人、`100 − rtp`
 * 是平台。「为什么才 25%」的答案不是「平台留着」，是「另外那一大份已经直接赔出去了」。
 *
 * <p><b>三个比例全部来自接口</b>（`config.jackpotContribPct` / `config.rtpPct` /
 * `config.seedRetainPct`），它们是 `milhar_economy_config` 表里按期生效的可调项 ——
 * 上一期 25%、下一期可能就是 30%。任何一处写死，调参那天页面会继续显示旧比例，
 * 而且没有任何地方会报错。
 */

export type FlowSegmentKey = 'pool' | 'fixed' | 'house' | 'winner' | 'seed';

export type FlowSegment = {
  key: FlowSegmentKey;
  /** 百分比，0–100。直接用作条的 `flexGrow`，也用作标签数值。 */
  pct: number;
};

/**
 * 把接口下发的比例转成数。
 *
 * <p><b>必须在 `Number()` 之前挡掉 null/undefined/空串</b> —— `Number(null)` 是 **0**，
 * 不是 NaN。少了这道守卫，后端没下发 `rtpPct`（老版本）时会算出「0% 回到玩家手里」
 * 并且画得有模有样。在一条以「每个数你都能自己核」立身的页面上，这比不画严重得多。
 *
 * <p>同一个坑还有一顶变装：`Number('   ')` 会先把空白 trim 掉再转数字，结果也是 **0**、
 * 也 `Number.isFinite`。字符串必须先 `trim()` 再判断是否为空 —— 否则一个后端传来的
 * 空白字符串会悄悄冒充成运营配的合法「0%」，而 `contribPct` / `seedRetainPct` 的 0
 * 本来就是合法值，没有任何下游业务边界能把这种假 0 拦下来。
 */
function pct(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** 浮点减法的残渣（88 − 25.1 = 62.900000000000006）不该渗进宽度和标签。 */
function trim(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * 每一期，投注额怎么分成三份。
 *
 * @param rtpPct     总返还率，已 ×100（如 `"88"`）。后端从当前生效的倍数表算出。
 * @param contribPct 进奖池比例，已 ×100（如 `"25"`）
 * @returns 恰好三段，顺序 `pool, fixed, house`；任一入参不可用或组合不可能时返回
 *   `null` —— 调用方应当**整段不渲染**，而不是退回一个两分图（那正是本次要消灭的
 *   「平台拿走 75%」误读）
 */
export function stakeFlow(
  rtpPct: string | number | null | undefined,
  contribPct: string | number | null | undefined,
): FlowSegment[] | null {
  const rtp = pct(rtpPct);
  const pool = pct(contribPct);
  if (rtp === null || pool === null) return null;
  // 返还率必须是正的且不超过 100 —— 超过 100 意味着平台那一段是负的
  if (rtp <= 0 || rtp > 100) return null;
  if (pool < 0) return null;
  // 进池比例不能大于总返还率，否则直接赔付那一段是负的
  if (pool > rtp) return null;
  return [
    { key: 'pool', pct: trim(pool) },
    { key: 'fixed', pct: trim(rtp - pool) },
    { key: 'house', pct: trim(100 - rtp) },
  ];
}

/**
 * **有人中头奖时**，奖池怎么分成两份。
 *
 * <p>标题必须带这个限定。没人中头奖时整个池子滚到下一期，这个分法根本不发生 ——
 * 把它写成「每一期」正是本次修掉的那个错。
 *
 * @param seedRetainPct 留作下一轮种子的比例，已 ×100（如 `"20"`）
 * @returns 恰好两段，顺序 `winner, seed`；不可用时 `null`
 */
export function winSplit(
  seedRetainPct: string | number | null | undefined,
): FlowSegment[] | null {
  const seed = pct(seedRetainPct);
  if (seed === null || seed < 0 || seed > 100) return null;
  return [
    { key: 'winner', pct: trim(100 - seed) },
    { key: 'seed', pct: trim(seed) },
  ];
}

/** 条上那个百分比标签。整数不带小数点，非整数保留一位。 */
export function formatFlowPct(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
}
